import React from 'react';
import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import styled from 'styled-components';

import { categoryLabel } from 'components/AiCompetitiveIntelligence/helpers';
import { getOrdinalSuffix } from 'components/View/utils';

import { NotableCommunity } from './notableCommunitiesLadder';

const Container = styled.div`
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--white-10);
`;

const TitleBar = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 20px;
  font-size: 12px;
  font-family: var(--font-bold);

  &.leading {
    background-color: rgba(10, 183, 118, 0.18);
    color: var(--positive-green);
  }

  &.lagging {
    background-color: rgba(232, 169, 71, 0.18);
    color: var(--inactive-orange);
  }
`;

const DOT_SIZE_PX = 6;

const Dot = styled.span`
  width: ${DOT_SIZE_PX}px;
  height: ${DOT_SIZE_PX}px;
  border-radius: 50%;
  background-color: currentColor;
  flex-shrink: 0;
  position: relative;
  top: calc((${DOT_SIZE_PX}px - 0.72em) / 2);
`;

const Table = styled.div`
  display: table;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

const Row = styled.div`
  display: table-row;
`;

const ClickableRow = styled(Row)`
  cursor: pointer;

  &:hover > div {
    background-color: var(--white-10);
  }
`;

const HeaderCell = styled.div`
  display: table-cell;
  width: 160px;
  font-size: 10px;
  color: var(--white-50);
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--white-15);
  text-align: left;
`;

const FirstHeaderCell = styled(HeaderCell)`
  width: 100%;
  padding-left: 20px;
`;

// Fixed table layout takes every column width from the header row, and the design gives SCORE and
// POSITION noticeably less room than STRONGEST and WEAKEST.
const NarrowHeaderCell = styled(HeaderCell)`
  width: 60px;
`;

const Cell = styled.div`
  display: table-cell;
  width: 160px;
  font-size: 12px;
  color: var(--white-50);
  padding: 10px 12px;
  border-bottom: 1px solid var(--white-10);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.last-row {
    border-bottom: none;
  }
`;

const FirstCell = styled(Cell)`
  width: 100%;
  padding-left: 20px;
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--white);
`;

const Reason = styled.div`
  font-size: 11px;
  font-family: var(--font-regular);
  color: var(--white-60);
`;

const ScoreCell = styled(Cell)`
  color: var(--white);
`;

const PositionCell = styled(Cell)`
  &.leading {
    color: var(--positive-green);
  }
  &.lagging {
    color: var(--inactive-orange);
  }
`;

const EmptyMessage = styled.div`
  color: var(--white-60);
  font-size: 12px;
  padding: 20px;
  text-align: center;
`;

const EMPTY_MESSAGE = {
  leading: 'There are no communities leading their market.',
  lagging: 'There are no communities lagging in their market.',
};

type Props = {
  title: string;
  accent: 'leading' | 'lagging';
  communities: NotableCommunity[];
};

const NotableCommunitiesTable = ({ title, accent, communities }: Props) => {
  const navigate = useNavigate();

  const openCommunityAICI = (communityId: number) => {
    navigate(`/ai-competitive-intelligence/overall-market-position?communityId=${communityId}`);
  };

  return (
    <Container data-test-id={`notable-communities-${accent}`}>
      <TitleBar className={accent}>
        <Dot />
        {title}
      </TitleBar>
      <Table>
        <Row>
          <FirstHeaderCell>COMMUNITY</FirstHeaderCell>
          <NarrowHeaderCell>SCORE</NarrowHeaderCell>
          <NarrowHeaderCell>POSITION</NarrowHeaderCell>
          <HeaderCell>STRONGEST</HeaderCell>
          <HeaderCell>WEAKEST</HeaderCell>
        </Row>
        {communities.map(({ row, reason }, index) => {
          const isLastRow = index === communities.length - 1;
          const competingCount = (row.competitors_count ?? 0) + 1;
          const strongest = categoryLabel(row.strongest_categories[0] ?? null) ?? '-';
          const weakest = categoryLabel(row.weakest_categories[0] ?? null) ?? '-';

          return (
            <ClickableRow key={row.community_id} onClick={() => openCommunityAICI(row.community_id)}>
              <FirstCell className={classNames({ 'last-row': isLastRow })} title={row.community_name}>
                {row.community_name}
                <Reason>{reason}</Reason>
              </FirstCell>
              <ScoreCell className={classNames({ 'last-row': isLastRow })}>{row.score ?? '-'}</ScoreCell>
              <PositionCell className={classNames(accent, { 'last-row': isLastRow })}>
                {row.rank ? `${getOrdinalSuffix(row.rank)} of ${competingCount}` : '-'}
              </PositionCell>
              <Cell className={classNames({ 'last-row': isLastRow })} title={strongest}>
                {strongest}
              </Cell>
              <Cell className={classNames({ 'last-row': isLastRow })} title={weakest}>
                {weakest}
              </Cell>
            </ClickableRow>
          );
        })}
      </Table>
      {communities.length === 0 && <EmptyMessage>{EMPTY_MESSAGE[accent]}</EmptyMessage>}
    </Container>
  );
};

export default NotableCommunitiesTable;
